import { useId, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

/** Cadre commun des démonstrations du site : une surface de l'app, sobre. */
export function DemoFrame({
  title,
  aside,
  children,
  footnote = "Données fictives",
  className,
}: {
  title: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  footnote?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-panel border border-border bg-surface shadow-paper",
        className,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-5">
        <div className="text-ui font-semibold text-foreground">{title}</div>
        {aside}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
      {footnote && (
        <p className="border-t border-border bg-muted/60 px-4 py-2.5 text-caption text-subtle-foreground sm:px-5">
          {footnote}
        </p>
      )}
    </div>
  );
}

/**
 * Choix exclusif compact (boutons `aria-pressed`) : la pastille glisse d'une
 * option à l'autre. Sans mouvement, elle change de place d'un coup.
 */
export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  className,
}: {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  className?: string;
}) {
  const id = useId();
  const reduce = useReducedMotion();
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        "inline-flex flex-wrap gap-0.5 rounded-lg border border-border bg-muted p-1",
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              "relative h-9 cursor-pointer rounded-md px-3 text-ui font-semibold transition-colors duration-150",
              "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring",
              active
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {active && (
              <motion.span
                layoutId={`segmented-${id}`}
                aria-hidden="true"
                className="absolute inset-0 rounded-md bg-surface shadow-card"
                transition={{
                  duration: reduce ? 0 : 0.24,
                  ease: [0.22, 1, 0.36, 1],
                }}
              />
            )}
            <span className="relative">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
